// TagClassificationDialog — create or edit a tag's semantic classification
// by hand. Saves through `PUT /api/ontology/tags/classification`
// (saveTagClassification), which persists onto the graph :Tag node with
// `model='manual'` so operator-set classifications are distinguishable from
// LLM-inferred ones.
//
// The form is class-dependent: numeric tags expose role / is-percentage /
// expected-range fields; boolean-enum tags expose good/bad/warning bucket
// pickers seeded from the states actually observed in the stats window
// (plus free-text for states not yet seen). `other` has no extra fields.

import { useEffect, useMemo, useState } from 'react'
import { Loader2, Plus } from 'lucide-react'
import {
  saveTagClassification,
  type TagClassificationRecord,
  type TagLlmClass,
  type TagLlmRole,
  type TagRecord
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { NativeSelect, NativeSelectOption } from '../../components/ui/native-select'
import { Switch } from '../../components/ui/switch'
import { Textarea } from '../../components/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '../../components/ui/toggle-group'
import { cn } from '../../lib/utils'

export interface TagClassificationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tag: TagRecord
  graphNodeId: string
  /** Pre-fills the form when editing an existing classification. */
  classification?: TagClassificationRecord
  /** States observed in the stats window — seeds the enum bucket picker. */
  observedStates?: string[]
  /** Fires with the stored record after a successful save. */
  onSaved?: (record: TagClassificationRecord) => void
}

type Bucket = 'good' | 'bad' | 'warning' | 'unset'

const ROLE_OPTIONS: { value: TagLlmRole; label: string }[] = [
  { value: 'sensor', label: 'Sensor' },
  { value: 'accumulator', label: 'Accumulator' },
  { value: 'setpoint', label: 'Setpoint' },
  { value: 'control_output', label: 'Control output' },
  { value: 'identifier_code', label: 'Identifier / code' },
  { value: 'other', label: 'Other' }
]

const BUCKET_TONE: Record<Exclude<Bucket, 'unset'>, string> = {
  good: 'data-[state=on]:bg-st-run-bg data-[state=on]:text-st-run',
  bad: 'data-[state=on]:bg-st-down-bg data-[state=on]:text-st-down',
  warning: 'data-[state=on]:bg-st-warn-bg data-[state=on]:text-st-warn'
}

interface FormState {
  tagClass: TagLlmClass
  role: TagLlmRole
  isPercentage: boolean
  rangeLow: string
  rangeHigh: string
  buckets: Map<string, Bucket>
  reasoning: string
}

function initialForm(
  classification: TagClassificationRecord | undefined,
  observedStates: string[]
): FormState {
  const buckets = new Map<string, Bucket>()
  for (const s of observedStates) buckets.set(s, 'unset')
  if (classification) {
    for (const s of classification.goodStates) buckets.set(s, 'good')
    for (const s of classification.badStates) buckets.set(s, 'bad')
    for (const s of classification.warningStates) buckets.set(s, 'warning')
  }
  return {
    tagClass: classification?.tagClass ?? 'numeric',
    role: classification?.role ?? 'sensor',
    isPercentage: classification?.isPercentage ?? false,
    rangeLow:
      classification?.expectedRangeLow != null ? String(classification.expectedRangeLow) : '',
    rangeHigh:
      classification?.expectedRangeHigh != null ? String(classification.expectedRangeHigh) : '',
    buckets,
    reasoning: classification?.reasoning ?? ''
  }
}

const TagClassificationDialog = ({
  open,
  onOpenChange,
  tag,
  graphNodeId,
  classification,
  observedStates = [],
  onSaved
}: TagClassificationDialogProps) => {
  const [form, setForm] = useState<FormState>(() => initialForm(classification, observedStates))
  const [newState, setNewState] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Re-seed the form whenever the dialog (re)opens for a possibly different
  // tag / classification.
  useEffect(() => {
    if (open) {
      setForm(initialForm(classification, observedStates))
      setNewState('')
      setError(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- observedStates is a render-fresh array; keying on open + identity inputs is intentional
  }, [open, tag.tagPath, classification])

  const rangeApplies = form.role === 'sensor' || form.role === 'control_output'

  const rangeError = useMemo(() => {
    if (form.tagClass !== 'numeric' || !rangeApplies) return null
    const hasLo = form.rangeLow.trim() !== ''
    const hasHi = form.rangeHigh.trim() !== ''
    if (!hasLo && !hasHi) return null
    const lo = Number(form.rangeLow)
    const hi = Number(form.rangeHigh)
    if (!hasLo || !hasHi || !Number.isFinite(lo) || !Number.isFinite(hi)) {
      return 'Set both range bounds (or neither).'
    }
    if (hi <= lo) return 'High bound must be greater than low bound.'
    return null
  }, [form.tagClass, form.rangeLow, form.rangeHigh, rangeApplies])

  const setBucket = (state: string, bucket: Bucket) => {
    setForm((prev) => {
      const next = new Map(prev.buckets)
      next.set(state, bucket)
      return { ...prev, buckets: next }
    })
  }

  const addState = () => {
    const s = newState.trim()
    if (!s || form.buckets.has(s)) return
    setBucket(s, 'unset')
    setNewState('')
  }

  const handleSave = async () => {
    if (rangeError) return
    setSaving(true)
    setError(null)
    try {
      const isNumeric = form.tagClass === 'numeric'
      const isEnum = form.tagClass === 'boolean_enum'
      const pick = (bucket: Bucket): string[] =>
        [...form.buckets.entries()].filter(([, b]) => b === bucket).map(([s]) => s)
      const hasRange = isNumeric && rangeApplies && form.rangeLow.trim() !== ''
      const stored = await saveTagClassification({
        nodeId: graphNodeId,
        tagPath: tag.tagPath,
        tagClass: form.tagClass,
        role: isNumeric ? form.role : null,
        isPercentage: isNumeric ? form.isPercentage : null,
        expectedRangeLow: hasRange ? Number(form.rangeLow) : null,
        expectedRangeHigh: hasRange ? Number(form.rangeHigh) : null,
        goodStates: isEnum ? pick('good') : null,
        badStates: isEnum ? pick('bad') : null,
        warningStates: isEnum ? pick('warning') : null,
        reasoning: form.reasoning.trim() || undefined
      })
      onSaved?.(stored)
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  const stateRows = [...form.buckets.keys()]

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="max-w-md" data-testid="tag-classification-dialog">
        <DialogHeader>
          <DialogTitle>
            {classification ? 'Modify classification' : 'Set classification'}
          </DialogTitle>
          <DialogDescription className="truncate font-mono text-11" title={tag.tagPath}>
            {tag.tagPath}
          </DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[55vh] flex-col gap-3.5 overflow-auto py-0.5 pr-1">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tag-class-select" className="text-12">
              Class
            </Label>
            <NativeSelect
              id="tag-class-select"
              size="sm"
              className="w-full text-13"
              value={form.tagClass}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, tagClass: e.target.value as TagLlmClass }))
              }
              data-testid="classification-class-select"
            >
              <NativeSelectOption value="numeric">Numeric</NativeSelectOption>
              <NativeSelectOption value="boolean_enum">Boolean / Enum</NativeSelectOption>
              <NativeSelectOption value="other">Other</NativeSelectOption>
            </NativeSelect>
          </div>

          {form.tagClass === 'numeric' ? (
            <>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="tag-role-select" className="text-12">
                  Role
                </Label>
                <NativeSelect
                  id="tag-role-select"
                  size="sm"
                  className="w-full text-13"
                  value={form.role}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, role: e.target.value as TagLlmRole }))
                  }
                  data-testid="classification-role-select"
                >
                  {ROLE_OPTIONS.map((o) => (
                    <NativeSelectOption key={o.value} value={o.value}>
                      {o.label}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </div>

              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="tag-percentage-switch" className="text-12">
                  Value is a percentage (0–100)
                </Label>
                <Switch
                  id="tag-percentage-switch"
                  checked={form.isPercentage}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({ ...prev, isPercentage: checked }))
                  }
                  data-testid="classification-percentage-switch"
                />
              </div>

              {rangeApplies ? (
                <div className="flex flex-col gap-1.5">
                  <Label className="text-12">Expected operating range</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      placeholder="Low"
                      className="h-8 text-13"
                      value={form.rangeLow}
                      onChange={(e) => setForm((prev) => ({ ...prev, rangeLow: e.target.value }))}
                      data-testid="classification-range-low"
                    />
                    <span className="text-ink-3">–</span>
                    <Input
                      type="number"
                      placeholder="High"
                      className="h-8 text-13"
                      value={form.rangeHigh}
                      onChange={(e) => setForm((prev) => ({ ...prev, rangeHigh: e.target.value }))}
                      data-testid="classification-range-high"
                    />
                  </div>
                  {rangeError ? (
                    <p
                      className="m-0 text-11 text-st-down"
                      data-testid="classification-range-error"
                    >
                      {rangeError}
                    </p>
                  ) : (
                    <p className="m-0 text-11 text-ink-3">
                      Optional. Readings outside this band flag the tag as a problem.
                    </p>
                  )}
                </div>
              ) : null}
            </>
          ) : null}

          {form.tagClass === 'boolean_enum' ? (
            <div className="flex flex-col gap-1.5">
              <Label className="text-12">State buckets</Label>
              {stateRows.length === 0 ? (
                <p className="m-0 text-11 text-ink-3">
                  No states observed in the stats window — add them below.
                </p>
              ) : null}
              <div className="flex flex-col gap-1" data-testid="classification-state-buckets">
                {stateRows.map((state) => {
                  const bucket = form.buckets.get(state) ?? 'unset'
                  return (
                    <div key={state} className="flex items-center justify-between gap-2">
                      <span className="min-w-0 truncate font-mono text-12 text-ink" title={state}>
                        {state}
                      </span>
                      <ToggleGroup
                        type="single"
                        variant="outline"
                        size="sm"
                        value={bucket === 'unset' ? '' : bucket}
                        onValueChange={(v) => setBucket(state, v === '' ? 'unset' : (v as Bucket))}
                        data-testid={`classification-bucket-${state}`}
                      >
                        <ToggleGroupItem
                          value="good"
                          className={cn('h-6 px-2 text-11', BUCKET_TONE.good)}
                        >
                          Good
                        </ToggleGroupItem>
                        <ToggleGroupItem
                          value="bad"
                          className={cn('h-6 px-2 text-11', BUCKET_TONE.bad)}
                        >
                          Bad
                        </ToggleGroupItem>
                        <ToggleGroupItem
                          value="warning"
                          className={cn('h-6 px-2 text-11', BUCKET_TONE.warning)}
                        >
                          Warning
                        </ToggleGroupItem>
                      </ToggleGroup>
                    </div>
                  )
                })}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Add a state…"
                  className="h-8 font-mono text-12"
                  value={newState}
                  onChange={(e) => setNewState(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addState()
                    }
                  }}
                  data-testid="classification-add-state-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 px-2"
                  onClick={addState}
                  disabled={!newState.trim()}
                  aria-label="Add state"
                  data-testid="classification-add-state-button"
                >
                  <Plus className="size-3.5" aria-hidden />
                </Button>
              </div>
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tag-reasoning" className="text-12">
              Note <span className="font-normal text-ink-3">(optional)</span>
            </Label>
            <Textarea
              id="tag-reasoning"
              rows={2}
              className="text-12"
              placeholder="Why this classification?"
              value={form.reasoning}
              onChange={(e) => setForm((prev) => ({ ...prev, reasoning: e.target.value }))}
              data-testid="classification-reasoning"
            />
          </div>

          {error ? (
            <p
              className="m-0 rounded-md bg-st-down-bg p-2 text-12 text-st-down"
              data-testid="classification-save-error"
            >
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button size="sm" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => void handleSave()}
            disabled={saving || rangeError !== null}
            data-testid="classification-save-button"
          >
            {saving ? (
              <>
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
                Saving…
              </>
            ) : (
              'Save classification'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default TagClassificationDialog
